<script setup>
import {storeToRefs} from 'pinia';
import {useAuthStore} from '@/stores/auth.store.js';
import {onMounted, ref, computed, watch} from "vue";
import axios from "axios";
import {useToast} from 'vue-toastification';
import {useRouter} from "vue-router";



const toast = useToast();

const authStore = useAuthStore();
const {user} = storeToRefs(authStore);
const router = useRouter();

const users = ref([]);
const rolesList = ref([]);
const currentPage = ref(1);
const perPage = ref(10);
const totalPages = ref(1);
const totalUsers = ref(0);

const loading = ref(false);
const search = ref('');

const showModal = ref(false);
const isEditMode = ref(false);
const showPassword = ref(false);

// Compose-email modal state. Opens an admin-only chooser that builds
// up a To: list from role-membership shortcuts and individual chips,
// then hands off to the OS mail client via mailto:.
const showEmailModal = ref(false);
const emailUsers = ref([]);            // [{name, email, role}]
const selectedEmails = ref([]);        // emails currently in the list

const usersByRole = computed(() => {
  const groups = new Map();
  for (const u of emailUsers.value) {
    const role = u.role || '— unassigned —';
    if (!groups.has(role)) groups.set(role, []);
    groups.get(role).push(u);
  }
  // Sort roles: admin first, then alphabetical, unassigned last.
  return Array.from(groups.entries()).sort(([a], [b]) => {
    if (a === b) return 0;
    if (a === 'admin') return -1;
    if (b === 'admin') return 1;
    if (a === '— unassigned —') return 1;
    if (b === '— unassigned —') return -1;
    return a.localeCompare(b);
  });
});

const openEmailModal = async () => {
  showEmailModal.value = true;
  selectedEmails.value = [];
  try {
    const res = await axios.get(`${import.meta.env.VITE_API_BACKEND_URL}/api/admin/users/email-list`, {
      withCredentials: true
    });
    emailUsers.value = res.data.users || [];
  } catch (err) {
    console.error(err);
    toast.error('Failed to load user emails');
  }
};

const addEmailsByRole = (roleName) => {
  const toAdd = emailUsers.value
    .filter(u => (u.role || '— unassigned —') === roleName)
    .map(u => u.email);
  const merged = new Set([...selectedEmails.value, ...toAdd]);
  selectedEmails.value = Array.from(merged);
};

const removeEmail = (email) => {
  selectedEmails.value = selectedEmails.value.filter(e => e !== email);
};

const clearEmails = () => {
  selectedEmails.value = [];
};

const openMailClient = () => {
  if (!selectedEmails.value.length) {
    toast.error('Add at least one address first');
    return;
  }
  // mailto: with comma-joined addresses in the To field. Most modern
  // mail clients accept this; the per-RFC limit is well above what
  // we'd hit for a team list.
  window.location.href = 'mailto:' + selectedEmails.value.join(',');
};

const currentUser = ref({
  id: null,
  name: '',
  email: '',
  password: '',
  role_id: null,
  send_email: false
});


const fetchUsers = async (page = 1) => {
  loading.value = true;
  try {
    const res = await axios.get(`${import.meta.env.VITE_API_BACKEND_URL}/api/admin/list-users`, {
      params: {page, per_page: perPage.value, search: search.value || undefined},
      withCredentials: true
    });

    users.value = res.data.data || res.data.users || res.data;
    currentPage.value = res.data.current_page || page;
    totalPages.value = res.data.total_pages || res.data.last_page || 1;
    totalUsers.value = res.data.total || 0;
  } catch (err) {
    console.error(err);
    toast.error('Failed to load users');
  } finally {
    loading.value = false;
  }
};

const fetchRoles = async () => {
  try {
    const res = await axios.get(`${import.meta.env.VITE_API_BACKEND_URL}/api/admin/roles`, {
      withCredentials: true
    });
    rolesList.value = res.data.data || res.data.roles || [];
  } catch (err) {
    console.error('Failed to load roles', err);
    toast.error('Failed to load roles list');
  }
};

const saveUser = async () => {
  try {
    if (!isEditMode.value && (!currentUser.value.password || currentUser.value.password.length < 8)) {
      toast.error('Password must be at least 8 characters long');
      return;
    }

    const payload = {
      name: currentUser.value.name,
      email: currentUser.value.email,
      role_id: currentUser.value.role_id,
      ...(!isEditMode.value && {
        password: currentUser.value.password,
        send_email: currentUser.value.send_email
      })
    };

    if (isEditMode.value && currentUser.value.id) {
      await axios.put(`${import.meta.env.VITE_API_BACKEND_URL}/api/admin/users/${currentUser.value.id}`, payload, {
        withCredentials: true
      });
      toast.success('User updated successfully');
    } else {
      const res = await axios.post(`${import.meta.env.VITE_API_BACKEND_URL}/api/admin/users`, payload, {
        withCredentials: true
      });
      // The API returns email_sent / email_error so the admin gets
      // explicit feedback on whether the password setup link went out.
      if (currentUser.value.send_email) {
        if (res.data?.email_sent) {
          toast.success(`User added — password setup email sent to ${currentUser.value.email}`);
        } else {
          toast.success('User added');
          toast.error(`Password setup email failed: ${res.data?.email_error || 'unknown error'}`);
        }
      } else {
        toast.success('User added successfully');
      }
    }

    showModal.value = false;
    fetchUsers(currentPage.value);
    resetForm();
  } catch (err) {
    console.error(err);
    toast.error(err.response?.data?.message || 'Error saving user');
  }
};

const deleteUser = async (id) => {
  if (!confirm('Are you sure you want to delete this user?')) return;

  try {
    await axios.delete(`${import.meta.env.VITE_API_BACKEND_URL}/api/admin/users/${id}`, {
      withCredentials: true
    });
    toast.success('User deleted successfully');
    fetchUsers(currentPage.value);
  } catch (err) {
    toast.error('Failed to delete user');
  }
};

const editUser = (u) => {
  isEditMode.value = true;
  currentUser.value = {
    id: u.id,
    name: u.name || '',
    email: u.email || '',
    password: '',
    role_id: u.role_id || null,
    send_email: false
  };
  showModal.value = true;
};

const openAddModal = () => {
  isEditMode.value = false;
  resetForm();
  showModal.value = true;
};

// 20 random chars from a 72-char alphabet using crypto.getRandomValues —
// ~123 bits of entropy, modulo bias negligible at this length.
// Visually-ambiguous glyphs (O 0 I l 1) excluded so generated values
// stay unambiguous if read aloud or hand-copied.
function generateRandomPassword() {
  const charset = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%^&*()-_=+';
  const len = 20;
  const arr = new Uint32Array(len);
  window.crypto.getRandomValues(arr);
  let pw = '';
  for (let i = 0; i < len; i++) {
    pw += charset[arr[i] % charset.length];
  }
  return pw;
}

async function copyToClipboard(value, successMsg = 'Password copied to clipboard') {
  if (!value) return;
  try {
    await navigator.clipboard.writeText(value);
    toast.success(successMsg);
  } catch (err) {
    toast.error('Could not copy — select the field and copy manually');
  }
}

// Add-user modal helpers — wire the shared utilities to currentUser.
const generatePassword = () => {
  currentUser.value.password = generateRandomPassword();
  showPassword.value = true;
};

const copyPassword = () => copyToClipboard(currentUser.value.password);

// Set-password modal state. Separate from currentUser because we don't
// want the admin's password edits leaking into / out of the add-user
// flow, and the target user is identified by id rather than the form.
const showPasswordModal = ref(false);
const passwordTarget = ref(null);          // { id, name, email }
const passwordForm = ref({ password: '', send_email: false });
const showPasswordModalPwd = ref(false);   // show/hide toggle local to this modal

const openPasswordModal = (u) => {
  passwordTarget.value = { id: u.id, name: u.name, email: u.email };
  passwordForm.value = { password: '', send_email: false };
  showPasswordModalPwd.value = false;
  showPasswordModal.value = true;
};

const generatePasswordForTarget = () => {
  passwordForm.value.password = generateRandomPassword();
  showPasswordModalPwd.value = true;
};

const copyPasswordForTarget = () => copyToClipboard(passwordForm.value.password);

const submitPassword = async () => {
  if (!passwordForm.value.password || passwordForm.value.password.length < 8) {
    toast.error('Password must be at least 8 characters long');
    return;
  }
  if (!passwordTarget.value?.id) return;
  try {
    const res = await axios.post(
      `${import.meta.env.VITE_API_BACKEND_URL}/api/admin/users/${passwordTarget.value.id}/password`,
      {
        password: passwordForm.value.password,
        send_email: passwordForm.value.send_email,
      },
      { withCredentials: true }
    );
    if (passwordForm.value.send_email) {
      if (res.data?.email_sent) {
        toast.success(`Password updated — setup email sent to ${passwordTarget.value.email}`);
      } else {
        toast.success('Password updated');
        toast.error(`Email failed: ${res.data?.email_error || 'unknown error'}`);
      }
    } else {
      toast.success('Password updated successfully');
    }
    showPasswordModal.value = false;
  } catch (err) {
    console.error(err);
    toast.error(err.response?.data?.error || 'Failed to update password');
  }
};

const resetForm = () => {
  currentUser.value = {
    id: null,
    name: '',
    email: '',
    password: '',
    role_id: null,
    send_email: false
  };
};

const changePage = (page) => {
  if (page < 1 || page > totalPages.value) return;
  fetchUsers(page);
};

const getRoleById = (roleId) => {
  if (!roleId) return null;
  return rolesList.value.find(role => role.id === roleId);
};
const pages = computed(() => Array.from({length: totalPages.value}, (_, i) => i + 1));

const isAdmin = computed(() => {
  return user.value?.role?.name === 'admin'
});

watch(() => isAdmin.value, (isAdminValue) => {
  if (isAdminValue === false && user.value !== null) {
    alert("You do not have access to user management.");
    router.push('/');
  }
}, { immediate: true });


const isLoadingUser = computed(() => !user.value);

const handleSearch = () => {
  currentPage.value = 1;
  fetchUsers(1);
};

onMounted(() => {
  if (!isAdmin.value) return;
  fetchUsers();
  fetchRoles();
});
</script>

<template>
  <div v-if="isLoadingUser" class="text-center py-5">
    <div class="spinner-border text-primary" role="status"></div>
    <p class="mt-3">Checking access rights...</p>
  </div>

  <div v-else-if="!isAdmin" class="text-center py-5">
    <div class="alert alert-danger">
      <h4>Access is denied</h4>
      <p>You do not have permission to view this page.</p>
      <button class="btn btn-primary mt-3" @click="router.push('/')">
        Return to home page
      </button>
    </div>
  </div>


  <div v-else class="container mt-4">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h1>User Management</h1>
      <div class="d-flex gap-2">
        <button class="btn btn-outline-primary" @click="openEmailModal">
          <i class="bi bi-envelope"></i> Compose Email
        </button>
        <button class="btn btn-success" @click="openAddModal">
          <i class="bi bi-plus-lg"></i> Add New User
        </button>
      </div>
    </div>

    <!-- Search -->
    <div class="row mb-3">
      <div class="col-md-6">
        <input v-model="search" @keyup.enter="handleSearch" type="text" class="form-control"
               placeholder="Search by name or email...">
      </div>
      <div class="col-md-2">
        <button class="btn btn-primary w-100" @click="handleSearch">Search</button>
      </div>
    </div>

    <!-- Table -->
    <div class="card">
      <div class="card-body">
        <div v-if="loading" class="text-center py-4">
          <div class="spinner-border text-primary" role="status"></div>
          <p class="mt-2">Loading users...</p>
        </div>

        <table v-else class="table table-hover align-middle">
          <thead class="table-light">
          <tr>
            <th>ID</th>
            <th>Name</th>
            <th>Email</th>
            <th>Role</th>
            <th class="text-end">Actions</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="u in users" :key="u.id">
            <td>{{ u.id }}</td>
            <td>{{ u.name }}</td>
            <td>{{ u.email }}</td>
            <td>
            <span
                class="badge"
                :class="getRoleById(u.role_id)?.name === 'admin' ? 'bg-danger' : 'bg-primary'">
                {{ getRoleById(u.role_id)?.name || 'No role' }}
            </span>
            </td>
            <td class="text-end">
              <button class="btn btn-sm btn-warning me-2" @click="editUser(u)" title="Edit">
                <i class="bi bi-pencil"></i>
              </button>
              <button class="btn btn-sm btn-secondary me-2" @click="openPasswordModal(u)" title="Set password">
                <i class="bi bi-key"></i>
              </button>
              <button class="btn btn-sm btn-danger" @click="deleteUser(u.id)" title="Delete">
                <i class="bi bi-trash"></i>
              </button>
            </td>
          </tr>
          <tr v-if="users.length === 0">
            <td colspan="5" class="text-center py-4 text-muted">No users found</td>
          </tr>
          </tbody>
        </table>
      </div>
    </div>


    <nav class="mt-4" v-if="totalPages > 1">
      <ul class="pagination justify-content-center">
        <li class="page-item" :class="{ disabled: currentPage === 1 }">
          <button class="page-link" @click="changePage(currentPage - 1)">Previous</button>
        </li>
        <li v-for="p in pages" :key="p" class="page-item" :class="{ active: p === currentPage }">
          <button class="page-link" @click="changePage(p)">{{ p }}</button>
        </li>
        <li class="page-item" :class="{ disabled: currentPage === totalPages }">
          <button class="page-link" @click="changePage(currentPage + 1)">Next</button>
        </li>
      </ul>
      <div class="text-center text-muted small">
        Showing {{ users.length }} of {{ totalUsers }} users
      </div>
    </nav>

    <div class="modal fade" :class="{ show: showModal }" tabindex="-1" style="display: block;" v-if="showModal">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ isEditMode ? 'Edit User' : 'Add New User' }}</h5>
            <button type="button" class="btn-close" @click="showModal = false"></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label class="form-label">Name</label>
              <input v-model="currentUser.name" class="form-control" required>
            </div>
            <div class="mb-3">
              <label class="form-label">Email</label>
              <input v-model="currentUser.email" type="email" class="form-control" required>
            </div>


            <div v-if="!isEditMode" class="mb-3">
              <label class="form-label">Password <span class="text-danger">*</span></label>
              <div class="input-group">
                <input
                    v-model="currentUser.password"
                    :type="showPassword ? 'text' : 'password'"
                    class="form-control"
                    placeholder="Minimum 8 characters"
                    required
                >
                <button class="btn btn-outline-secondary" type="button"
                        @click="generatePassword" title="Generate strong password">
                  <i class="bi bi-shuffle"></i>
                </button>
                <button class="btn btn-outline-secondary" type="button"
                        :disabled="!currentUser.password"
                        @click="copyPassword" title="Copy to clipboard">
                  <i class="bi bi-clipboard"></i>
                </button>
                <button class="btn btn-outline-secondary" type="button"
                        @click="showPassword = !showPassword"
                        :title="showPassword ? 'Hide password' : 'Show password'">
                  <i class="bi" :class="showPassword ? 'bi-eye-slash' : 'bi-eye'"></i>
                </button>
              </div>
              <small class="text-muted">Password must be at least 8 characters long</small>
            </div>

            <div v-if="!isEditMode" class="mb-3 form-check">
              <input v-model="currentUser.send_email"
                     type="checkbox"
                     id="sendPasswordEmail"
                     class="form-check-input">
              <label class="form-check-label" for="sendPasswordEmail">
                Email a password setup link to the user
              </label>
              <div class="text-muted small">
                Sends a one-time link the user can click to set their own password. The
                password you typed above is still set on the account, so you can also
                share it directly if you prefer.
              </div>
            </div>

            <div class="mb-3">
              <label class="form-label">Role</label>
              <select v-model="currentUser.role_id" class="form-select" required>
                <option value="" disabled>Select a role</option>
                <option v-for="role in rolesList" :key="role.id" :value="role.id">
                  {{ role.name }}
                </option>
              </select>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="showModal = false">Cancel</button>
            <button type="button" class="btn btn-primary" @click="saveUser">
              {{ isEditMode ? 'Save Changes' : 'Add User' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="modal-backdrop fade show" v-if="showModal" @click="showModal = false"></div>

    <!-- Compose-email modal -->
    <div class="modal fade" :class="{ show: showEmailModal }" tabindex="-1" style="display: block;" v-if="showEmailModal">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Compose Email</h5>
            <button type="button" class="btn-close" @click="showEmailModal = false"></button>
          </div>
          <div class="modal-body">

            <div class="mb-3">
              <label class="form-label">Add by role</label>
              <div class="d-flex flex-wrap gap-2">
                <button v-for="[roleName, members] in usersByRole"
                        :key="roleName"
                        type="button"
                        class="btn btn-sm btn-outline-secondary"
                        @click="addEmailsByRole(roleName)">
                  + {{ roleName }} ({{ members.length }})
                </button>
                <span v-if="!usersByRole.length" class="text-muted small">Loading…</span>
              </div>
            </div>

            <div class="mb-3">
              <div class="d-flex justify-content-between align-items-center">
                <label class="form-label mb-0">
                  Recipients
                  <span class="text-muted small">({{ selectedEmails.length }})</span>
                </label>
                <button type="button"
                        class="btn btn-sm btn-link text-muted p-0"
                        :disabled="!selectedEmails.length"
                        @click="clearEmails">Clear all</button>
              </div>
              <div class="recipients-box border rounded p-2 mt-1">
                <div v-if="!selectedEmails.length" class="text-muted small">
                  Click a role above to start adding addresses.
                </div>
                <span v-for="email in selectedEmails"
                      :key="email"
                      class="badge bg-light text-dark border me-1 mb-1">
                  {{ email }}
                  <button type="button"
                          class="btn-close btn-close-sm ms-2"
                          aria-label="Remove"
                          @click="removeEmail(email)"></button>
                </span>
              </div>
            </div>

          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="showEmailModal = false">Cancel</button>
            <button type="button"
                    class="btn btn-primary"
                    :disabled="!selectedEmails.length"
                    @click="openMailClient">
              <i class="bi bi-envelope"></i>
              Open in Mail Client
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="modal-backdrop fade show" v-if="showEmailModal" @click="showEmailModal = false"></div>

    <!-- Set-password modal -->
    <div class="modal fade" :class="{ show: showPasswordModal }" tabindex="-1" style="display: block;" v-if="showPasswordModal">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Set Password</h5>
            <button type="button" class="btn-close" @click="showPasswordModal = false"></button>
          </div>
          <div class="modal-body">

            <div class="mb-3">
              <div class="text-muted small">For</div>
              <div><strong>{{ passwordTarget?.name || '—' }}</strong></div>
              <div class="text-muted small">{{ passwordTarget?.email }}</div>
            </div>

            <div class="mb-3">
              <label class="form-label">New password <span class="text-danger">*</span></label>
              <div class="input-group">
                <input
                    v-model="passwordForm.password"
                    :type="showPasswordModalPwd ? 'text' : 'password'"
                    class="form-control"
                    placeholder="Minimum 8 characters"
                    required
                >
                <button class="btn btn-outline-secondary" type="button"
                        @click="generatePasswordForTarget" title="Generate strong password">
                  <i class="bi bi-shuffle"></i>
                </button>
                <button class="btn btn-outline-secondary" type="button"
                        :disabled="!passwordForm.password"
                        @click="copyPasswordForTarget" title="Copy to clipboard">
                  <i class="bi bi-clipboard"></i>
                </button>
                <button class="btn btn-outline-secondary" type="button"
                        @click="showPasswordModalPwd = !showPasswordModalPwd"
                        :title="showPasswordModalPwd ? 'Hide password' : 'Show password'">
                  <i class="bi" :class="showPasswordModalPwd ? 'bi-eye-slash' : 'bi-eye'"></i>
                </button>
              </div>
              <small class="text-muted">Password must be at least 8 characters long</small>
            </div>

            <div class="mb-3 form-check">
              <input v-model="passwordForm.send_email"
                     type="checkbox"
                     id="sendPasswordResetEmail"
                     class="form-check-input">
              <label class="form-check-label" for="sendPasswordResetEmail">
                Email a password setup link to the user
              </label>
              <div class="text-muted small">
                The new password is set on the account immediately. The optional email
                lets the user click a one-time link and set a password of their own,
                overriding what you typed here.
              </div>
            </div>

          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="showPasswordModal = false">Cancel</button>
            <button type="button" class="btn btn-primary" @click="submitPassword">
              Update Password
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="modal-backdrop fade show" v-if="showPasswordModal" @click="showPasswordModal = false"></div>
  </div>
</template>

<style scoped>
.recipients-box {
  min-height: 80px;
  max-height: 240px;
  overflow-y: auto;
  background: #fff;
}
.btn-close-sm {
  width: 0.6rem;
  height: 0.6rem;
}
</style>